import { useFieldArray, type FieldErrors, type Control } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
    ControlledTextField,
    SimpleButton,
    ErrorMessage,
    ControlledSingleSelect,
    ControlledSwitch,
} from '@/src/ui';
import type { QtiGraphingQuestionDTO } from '@/src/lib';

interface GraphLinesEditorProps {
    question: QtiGraphingQuestionDTO;
    control: Control<QtiGraphingQuestionDTO>;
    errors: FieldErrors<QtiGraphingQuestionDTO>;
    lineOptions: { id: 'segment' | 'ray' | 'line'; name: string }[];
}

export default function GraphLinesEditor({
    question,
    control,
    errors,
    lineOptions,
}: GraphLinesEditorProps) {
    const minLines = 0;
    const maxLines = 10;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'lines',
    });

    const handleAdd = () => {
        if (fields.length >= maxLines) return;

        let nextIndex = 1;
        while (fields.find((field) => field.label === `L${nextIndex}`)) {
            nextIndex++;
        }
        append({
            id: uuidv4(),
            label: `L${nextIndex}`,
            style: lineOptions[0]?.id || 'segment',
            x1: -1,
            y1: -1,
            x2: 1,
            y2: 1,
            isHidden: true,
        });
    };

    return (
        <>
            {fields.length === 0 && (
                <div className="text-sm text-gray-500">
                    No lines yet. Click <b>Add Line</b> to begin.
                </div>
            )}

            {fields.map((field, index) => (
                <div
                    key={field.id}
                    className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between border border-gray-300 rounded bg-gray-50 p-3 text-sm mb-2"
                >
                    <div className="flex flex-col gap-3 md:flex-row md:flex-wrap">
                        <ControlledTextField
                            name={`lines.${index}.label`}
                            control={control}
                            label="Label"
                            placeholder="e.g. ℓ"
                            error={errors.lines?.[index]?.label}
                        />

                        <ControlledSingleSelect
                            name={`lines.${index}.style`}
                            control={control}
                            label="Style"
                            options={lineOptions}
                            error={errors.lines?.[index]?.style}
                        />

                        <ControlledTextField
                            name={`lines.${index}.x1`}
                            control={control}
                            label="x1"
                            type="number"
                            error={errors.lines?.[index]?.x1}
                        />
                        <ControlledTextField
                            name={`lines.${index}.y1`}
                            control={control}
                            label="y1"
                            type="number"
                            error={errors.lines?.[index]?.y1}
                        />
                        <ControlledTextField
                            name={`lines.${index}.x2`}
                            control={control}
                            label="x2"
                            type="number"
                            error={errors.lines?.[index]?.x2}
                        />
                        <ControlledTextField
                            name={`lines.${index}.y2`}
                            control={control}
                            label="y2"
                            type="number"
                            error={errors.lines?.[index]?.y2}
                        />

                        <ControlledSwitch
                            name={`lines.${index}.isHidden`}
                            control={control}
                            label={
                                question.lines[index]?.isHidden
                                    ? 'Hidden (Scored)'
                                    : 'Visible (Not Scored)'
                            }
                        />
                    </div>

                    <div className="flex gap-1 pt-1">
                        <SimpleButton
                            type="button"
                            color="error"
                            variant="outlined"
                            disabled={fields.length <= minLines}
                            onClick={() => remove(index)}
                        >
                            Remove
                        </SimpleButton>
                    </div>

                    <ErrorMessage error={errors.lines?.[index] as any} />
                </div>
            ))}

            {/* Group-level errors */}
            <ErrorMessage error={errors.lines?.root} />
            <ErrorMessage error={errors.lines} />

            <SimpleButton
                type="button"
                color="primary"
                onClick={handleAdd}
                className="bg-blue-500 text-white px-4 py-2 rounded"
                disabled={fields.length >= maxLines}
            >
                Add Line
            </SimpleButton>
        </>
    );
}
